import type { FC, ReactNode } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Inline, Stack } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Button, { LinkButton } from '@atlaskit/button/new';

import { PremiumFeature } from './PremiumFeatureTypes';

const i18n = defineMessages({
	automationModalTitle: {
		id: 'premium-trial.bite-sized.automation.modal.title',
		defaultMessage: 'Say goodbye 👋 to manual tasks with automation',
		description: 'Title for automation modal',
	},
	automationModalDescription: {
		id: 'premium-trial.bite-sized.automation.modal.description',
		defaultMessage:
			'Automation acts as your virtual assistant, handling manual tasks so you don’t have to.',
		description: 'Description for automation modal',
	},
	automationModalBodyText1: {
		id: 'premium-trial.bite-sized.automation.modal.body.text1',
		defaultMessage: '<strong>38%</strong> of customers save time with Automation',
		description: 'Body text first line for automation modal',
	},
	automationModalBodyText2: {
		id: 'premium-trial.bite-sized.automation.modal.body.text2',
		defaultMessage: '<strong>60%</strong> users use Automation are non-technical teams',
		description: 'Body text second line for automation modal',
	},
	automationModalPrimaryActionText: {
		id: 'premium-trial.bite-sized.automation.modal.primary-action',
		defaultMessage: 'Go to automation',
		description: 'Primary action text for automation modal',
	},
	automationModalSecondaryActionText: {
		id: 'premium-trial.bite-sized.automation.modal.secondary-action',
		defaultMessage: 'See more features',
		description: 'Secondary action text for automation modal',
	},
});

export type PremiumFeatureTastingAutomationLeftPanelProps = {
	onClickSecondaryCTA: {
		setShowAutomation: React.Dispatch<React.SetStateAction<boolean>>;
		setFeatureShowing: React.Dispatch<React.SetStateAction<PremiumFeature>>;
	};
	automationLink: string;
};

export const PremiumFeatureTastingAutomationLeftPanel: FC<
	PremiumFeatureTastingAutomationLeftPanelProps
> = ({ onClickSecondaryCTA, automationLink }) => {
	const { setShowAutomation, setFeatureShowing } = onClickSecondaryCTA;

	const handleSecondaryCTA = () => {
		setFeatureShowing(PremiumFeature.ANALYTICS);
		setShowAutomation(false);
	};

	return (
		<>
			<Stack space="space.100">
				<Heading size="large">
					<FormattedMessage {...i18n.automationModalTitle} />
				</Heading>
				<Box>
					<FormattedMessage {...i18n.automationModalDescription} />
				</Box>
			</Stack>
			<Stack space="space.200">
				<Box>
					<FormattedMessage
						{...i18n.automationModalBodyText1}
						values={{
							strong: (str: ReactNode[]) => <strong>{str}</strong>,
						}}
					/>
				</Box>
				<Box>
					<FormattedMessage
						{...i18n.automationModalBodyText2}
						values={{
							strong: (str: ReactNode[]) => <strong>{str}</strong>,
						}}
					/>
				</Box>
			</Stack>
			<Stack alignBlock="end" alignInline="end" grow="fill">
				<Inline space="space.200">
					<Button onClick={() => handleSecondaryCTA()}>
						<FormattedMessage {...i18n.automationModalSecondaryActionText} />
					</Button>
					<LinkButton appearance="primary" href={automationLink}>
						<FormattedMessage {...i18n.automationModalPrimaryActionText} />
					</LinkButton>
				</Inline>
			</Stack>
		</>
	);
};
